import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import Navbar from './Navbar';
import Footer from './Footer';
import { mediaUrl } from '../utils/media';
import { DONATE_URL } from '../utils/donate';

interface Cause {
  category: string;
  title: string;
  description: string;
  goalUsd: number;
  raisedUsd: number;
  percentage: number;
  image: string;
  detailsPath?: string;
}

const USD_TO_INR_RATE = 83;

const formatInrFromUsd = (usdAmount: number): string => {
  const inrAmount = Math.round(usdAmount * USD_TO_INR_RATE);
  return `₹${inrAmount.toLocaleString('en-IN')}`;
};

const CausesPage: React.FC = () => {
  useEffect(() => {
    // Initialize AOS (Animate On Scroll)
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false,
    });

    // Hide spinner after page loads
    const spinner = document.getElementById('spinner');
    if (spinner) {
      setTimeout(() => {
        spinner.classList.remove('show');
      }, 500);
    }

    // Back to top button functionality
    const backToTop = document.querySelector('.back-to-top');
    if (backToTop) {
      const handleScroll = () => {
        if (window.scrollY > 300) {
          (backToTop as HTMLElement).style.display = 'block';
        } else {
          (backToTop as HTMLElement).style.display = 'none';
        }
      };

      window.addEventListener('scroll', handleScroll);

      backToTop.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const causes: Cause[] = [
    {
      category: 'Education',
      title: 'Education For Indian Children',
      description:
        'Supporting children across India with better schools, digital learning tools, and access to quality teachers so every child can build a brighter future.',
      goalUsd: 10000,
      raisedUsd: 9542,
      percentage: 90,
      image: mediaUrl('Photo from Anand Mashatte(23).jpg'),
      detailsPath: '/causes/education-indian-children',
    },
    {
      category: 'Tree Plantation',
      title: 'Plant Trees, Save Lives',
      description: 'Planting trees to combat climate change, restore ecosystems, and create greener communities for future generations.',
      goalUsd: 10000,
      raisedUsd: 7250,
      percentage: 72,
      image: mediaUrl('Photo from Anand Mashatte(39).jpg'),
      detailsPath: '/causes/tree-plantation',
    },
  ];

  return (
    <>
      <div
        id="spinner"
        className="show bg-white position-fixed translate-middle w-100 vh-100 top-50 start-50 d-flex align-items-center justify-content-center"
      >
        <div className="spinner-grow text-primary" role="status"></div>
      </div>

      <Navbar />

      {/* Page Header Start */}
      <div
        className="container-fluid page-header causes-page-header mb-5"
        data-aos="fade"
        data-aos-delay="100"
        style={{ background: `linear-gradient(rgba(0, 29, 35, .75), rgba(0, 29, 35, .75)), url("${mediaUrl('Photo from Anand Mashatte(47).jpg')}") center center / cover no-repeat` }}
      >
        <div className="container text-center">
          <h1 className="display-4 text-white animated slideInDown mb-4" data-aos="fade-down">
            Causes
          </h1>
          <nav aria-label="breadcrumb animated slideInDown" data-aos="fade-down" data-aos-delay="200">
            <ol className="breadcrumb justify-content-center mb-0">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-white">Pages</span>
              </li>
              <li className="breadcrumb-item text-primary active" aria-current="page">
                Causes
              </li>
            </ol>
          </nav>
        </div>
      </div>
      {/* Page Header End */}

      {/* Causes Start */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center mx-auto mb-5" data-aos="fade-up" data-aos-delay="100" style={{ maxWidth: '500px' }}>
            <div className="d-inline-block rounded-pill bg-secondary text-primary py-1 px-3 mb-3">
              Feature Causes
            </div>
            <h1 className="display-6 mb-5">Every Child Deserves The Opportunity To Learn</h1>
          </div>
          <div className="causes-grid">
            {causes.map((cause, index) => (
              <div key={index} data-aos="fade-up" data-aos-delay={`${100 + index * 200}`}>
                <div className="causes-item d-flex flex-column bg-light border-top border-5 border-primary rounded-top overflow-hidden h-100">
                  <div className="text-center p-4 pt-0">
                    <div className="d-inline-block bg-primary text-white rounded-bottom fs-5 pb-1 px-3 mb-4">
                      <small>{cause.category}</small>
                    </div>
                    <h5 className="mb-3">{cause.title}</h5>
                    <p>{cause.description}</p>
                    <div className="causes-progress bg-white p-3 pt-2">
                      <div className="d-flex justify-content-between">
                        <p className="text-dark">
                          {formatInrFromUsd(cause.goalUsd)} <small className="text-body">Goal</small>
                        </p>
                        <p className="text-dark">
                          {formatInrFromUsd(cause.raisedUsd)} <small className="text-body">Raised</small>
                        </p>
                      </div>
                      <div className="progress">
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${cause.percentage}%` }}
                          aria-valuenow={cause.percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <span>{cause.percentage}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="position-relative mt-auto">
                    <img className="img-fluid" src={cause.image} alt={cause.title} />
                    <div className="causes-overlay">
                      {cause.detailsPath ? (
                        <Link className="btn btn-outline-primary" to={cause.detailsPath}>
                          Read More
                          <div className="d-inline-flex btn-sm-square bg-primary text-white rounded-circle ms-2">
                            <i className="fa fa-arrow-right"></i>
                          </div>
                        </Link>
                      ) : (
                        <a
                          className="btn btn-outline-primary"
                          href={DONATE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Donate Now
                          <div className="d-inline-flex btn-sm-square bg-primary text-white rounded-circle ms-2">
                            <i className="fa fa-arrow-right"></i>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Causes End */}

      <Footer />

      <button type="button" className="btn btn-lg btn-primary btn-lg-square back-to-top" aria-label="Back to top">
        <i className="bi bi-arrow-up"></i>
      </button>
    </>
  );
};

export default CausesPage;

